import { useEffect, useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonMenuButton,
  IonSearchbar, IonSegment, IonSegmentButton, IonLabel, IonSpinner, IonButton,
  IonActionSheet, IonIcon, IonChip,
} from '@ionic/react';
import { addOutline, ellipsisHorizontalOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import type { PublicPost } from '@ai-media/shared';
import { useContentStore } from '@/store/content.store';
import { toast } from '@/store/ui.store';
import { apiErrorMessage } from '@/lib/api';
import PostCard from '@/components/PostCard';
import EmptyState from '@/components/EmptyState';

const STATUS_FILTERS = ['', 'scheduled', 'queued', 'approving', 'posted', 'failed'] as const;

export default function PostsPage() {
  const { t } = useTranslation();
  const posts = useContentStore((s) => s.posts);
  const loading = useContentStore((s) => s.loading);
  const fetchPosts = useContentStore((s) => s.fetchPosts);
  const approve = useContentStore((s) => s.approve);
  const publishNow = useContentStore((s) => s.publishNow);
  const cancel = useContentStore((s) => s.cancel);
  const remove = useContentStore((s) => s.remove);

  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>('');
  const [search, setSearch] = useState('');
  const [target, setTarget] = useState<PublicPost | null>(null);

  useEffect(() => {
    void fetchPosts({ status: status || undefined, search: search || undefined });
  }, [status, search, fetchPosts]);

  const act = async (action: 'approve' | 'publish' | 'cancel' | 'delete') => {
    if (!target) return;
    const id = target.id;
    setTarget(null);
    try {
      if (action === 'approve') await approve(id);
      if (action === 'publish') await publishNow(id);
      if (action === 'cancel') await cancel(id);
      if (action === 'delete') await remove(id);
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <IonTitle>{t('content.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton routerLink="/app/composer">
              <IonIcon slot="icon-only" icon={addOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar placeholder={t('common.search')} value={search} debounce={350}
            onIonInput={(event) => setSearch((event.target as HTMLIonSearchbarElement).value as string)} />
        </IonToolbar>
        <IonToolbar>
          <IonSegment value={status} scrollable
            onIonChange={(event) => setStatus((event.target as HTMLIonSegmentElement).value as (typeof STATUS_FILTERS)[number])}>
            {STATUS_FILTERS.map((option) => (
              <IonSegmentButton key={option || 'all'} value={option}>
                <IonLabel>{option ? t(`status.${option}`) : t('common.filters')}</IonLabel>
              </IonSegmentButton>
            ))}
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {loading && posts.length === 0 ? (
          <div className="empty-state"><IonSpinner /></div>
        ) : posts.length === 0 ? (
          <EmptyState icon={addOutline} title={t('content.noPosts')} body={t('content.createFirstPost')}
            action={<IonButton routerLink="/app/composer">{t('content.newPost')}</IonButton>} />
        ) : (
          posts.map((post) => (
            <div key={post.id} style={{ position: 'relative' }}>
              <PostCard post={post} />
              <IonChip style={{ position: 'absolute', top: 10, right: 12 }} onClick={() => setTarget(post)}>
                <IonIcon icon={ellipsisHorizontalOutline} />
              </IonChip>
            </div>
          ))
        )}
        <IonActionSheet
          isOpen={target !== null}
          onDidDismiss={() => setTarget(null)}
          header={target?.title ?? undefined}
          buttons={[
            ...(target?.status === 'approving'
              ? [{ text: t('content.approve'), handler: () => void act('approve') }]
              : []),
            ...(target && target.status !== 'posted' && target.status !== 'posting'
              ? [{ text: t('content.publish'), handler: () => void act('publish') }]
              : []),
            ...(target && target.status !== 'posted'
              ? [{ text: t('content.cancel'), role: 'destructive', handler: () => void act('cancel') }]
              : []),
            ...(target
              ? [{ text: t('postDetails.delete'), role: 'destructive', handler: () => void act('delete') }]
              : []),
            { text: t('common.cancel'), role: 'cancel' },
          ]}
        />
      </IonContent>
    </IonPage>
  );
}
